import { useEffect, useRef, useState } from 'react';
import { identityApi } from '@repo/api/clients/identity.api';
import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import { useNavigate, useParams } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Operator } from '@repo/api/schemas/agentPolicy.schema';

import { TOKENS } from '../../../../shared/constants';
import {
  useAgentPolicyById,
  useCreatePolicy,
  useEvaluationsPreview,
  useUpdatePolicy,
} from '../../hooks/useAgentPolicy';
import { isRowIncomplete } from '../../utils/conditionFormUtils';
import {
  type ConditionRow,
  createEmptyConditionRow,
  DEFAULT_OWNERS,
  isOwnerRowValid,
  type OwnerRow,
} from '../../utils/policyFormUtils';

import ActionContent from './ActionContent';
import ConditionContent from './ConditionContent';
import PolicyFormSteps, { type PolicyFormStep } from './PolicyFormSteps';
import PolicyNameUpdateModal from './PolicyNameUpdateModal';
import TriggerSelectContent, { type TriggerType } from './TriggerSelectContent';

import './PolicyForm.css';

const UNTITLED_POLICY_MSG = {
  id: 'PolicyForm.untitledPolicy',
  defaultMessage: 'Untitled Rule',
  description: 'Default policy name when creating a new policy',
} as const;

export default function PolicyForm() {
  const navigate = useNavigate();
  const intl = useIntl();

  // Try to get policyId if we're in edit mode, otherwise undefined
  let policyId: string | undefined;
  try {
    const params = useParams({ from: '/_agent/policies/edit/$policyId' });
    policyId = params.policyId;
  } catch {
    // Not in edit mode, creating new policy
    policyId = undefined;
  }
  const [policyTitle, setPolicyTitle] = useState(() =>
    intl.formatMessage(UNTITLED_POLICY_MSG)
  );
  const [policyDescription, setPolicyDescription] = useState('');
  const [selectedStep, setSelectedStep] = useState<PolicyFormStep>(1);
  const [triggerSubStep, setTriggerSubStep] = useState<TriggerType | null>(
    null
  );
  const [conditionData, setConditionData] = useState<{
    conditions: ConditionRow[];
  } | null>(null);
  const [ownerData, setOwnerData] = useState<OwnerRow[]>(() => DEFAULT_OWNERS);
  const [isEditPolicyModalOpen, setIsEditPolicyModalOpen] = useState(false);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [isResolvingOwners, setIsResolvingOwners] = useState(false);
  const [banner, setBanner] = useState<{
    colorTheme: 'Success' | 'Critical';
    title: string;
  } | null>(null);

  // --- Preview count caching: avoid re-calling heavy API when conditions haven't changed ---
  const [matchingAgentCount, setMatchingAgentCount] = useState(0);
  const previewMutation = useEvaluationsPreview();
  const lastConditionsKeyRef = useRef<string | null>(null);

  // The payload sent to the API (only valid/complete condition rows)
  const previewPayload = {
    conditions: {
      operator: 'AND' as const,
      conditions: (conditionData?.conditions ?? [])
        .filter((c) => !isRowIncomplete(c))
        .map((c) => ({
          field: c.attribute!.trim(),
          operator: c.operator as Operator,
          value: c.value ?? '',
        })),
    },
    triggerType:
      triggerSubStep === 'labels_change'
        ? ('LABEL_MODIFICATION' as const)
        : ('AGENT_REGISTERED' as const),
    domain: 'AGENT_SECURITY' as const,
  };

  // Cache key based on RAW condition rows + trigger, so any edit invalidates
  const conditionsKey = JSON.stringify({
    conditions: conditionData?.conditions ?? [],
    triggerSubStep,
  });

  // Fetch preview when arriving at step 2, but only if conditions changed
  useEffect(() => {
    if (selectedStep !== 2) {
      return;
    }
    if (conditionsKey === lastConditionsKeyRef.current) {
      return;
    }

    lastConditionsKeyRef.current = conditionsKey;
    previewMutation.mutate(previewPayload, {
      onSuccess: (data) => {
        setMatchingAgentCount(data.matchingAgentCount ?? 0);
      },
    });
  }, [selectedStep, conditionsKey]);

  const conditionCount =
    conditionData?.conditions.filter((c) => !isRowIncomplete(c)).length ?? 0;

  const businessOwnerCount = ownerData.filter(
    (row) =>
      isOwnerRowValid(row) && (row.role ?? '').toLowerCase() === 'business'
  ).length;
  const technicalOwnerCount = ownerData.filter(
    (row) =>
      isOwnerRowValid(row) && (row.role ?? '').toLowerCase() === 'technical'
  ).length;
  const validOwnerRows = ownerData.filter(isOwnerRowValid);
  const hasNoValidOwner = validOwnerRows.length === 0;

  const untitledPolicyLabel = intl.formatMessage(UNTITLED_POLICY_MSG);
  const isPolicyNameInvalid =
    !policyTitle.trim() || policyTitle.trim() === untitledPolicyLabel;
  const hasNoValidCondition = conditionCount === 0;
  const isSaveDisabled =
    isPolicyNameInvalid || hasNoValidCondition || hasNoValidOwner;

  const saveDisabledReasons: string[] = [];
  if (isPolicyNameInvalid) {
    saveDisabledReasons.push(
      intl.formatMessage({
        id: 'HcLltF',
        defaultMessage: 'Add the rule name',
      })
    );
  }
  if (hasNoValidCondition) {
    saveDisabledReasons.push(
      intl.formatMessage({
        id: 'e2cZV+',
        defaultMessage: 'Add at least one valid condition',
      })
    );
  }
  if (hasNoValidOwner) {
    saveDisabledReasons.push(
      intl.formatMessage({
        id: 'H22WTv',
        defaultMessage: 'Add at least one valid owner',
      })
    );
  }
  const saveTooltipText = saveDisabledReasons
    .map((r) => `\u2022 ${r}`)
    .join('\n');

  const createPolicyMutate = useCreatePolicy({
    onError: () =>
      setBanner({
        colorTheme: 'Critical',
        title: intl.formatMessage({
          id: 'srEyul',
          defaultMessage: 'Failed to create rule. Please try again.',
          description: 'Error banner message on policy creation failure',
        }),
      }),
    onSuccess: () => {
      sessionStorage.setItem(
        'policySuccessBanner',
        intl.formatMessage({
          id: 'D3byb/',
          defaultMessage: 'Rule created successfully.',
          description: 'Success banner message on policy creation',
        })
      );
      navigate({ to: '/policies' });
    },
  });

  const updatePolicyMutate = useUpdatePolicy({
    onError: () =>
      setBanner({
        colorTheme: 'Critical',
        title: intl.formatMessage({
          id: 'DQVMMQ',
          defaultMessage: 'Failed to update rule. Please try again.',
          description: 'Error banner message on policy update failure',
        }),
      }),
    onSuccess: () => {
      sessionStorage.setItem(
        'policySuccessBanner',
        intl.formatMessage({
          id: 'hyB2O6',
          defaultMessage: 'Rule updated successfully.',
          description: 'Success banner message on policy update',
        })
      );
      navigate({ to: '/policies' });
    },
  });

  //  fetch policy data for edit mode
  const { data: policyData, isLoading: isPolicyLoading } = useAgentPolicyById(
    policyId || ''
  );
  const hasInitializedConditionsFromPolicy = useRef(false);
  const hasInitializedOwnersFromPolicy = useRef(false);

  // Update form fields, conditions, and owners when policy data is loaded (edit mode)
  useEffect(() => {
    if (!policyData) {
      return;
    }

    setPolicyTitle(policyData.name);
    setPolicyDescription(policyData.description);
    setApplyToExisting(policyData.batchModeEnabled ?? false);

    if (policyId && !hasInitializedConditionsFromPolicy.current) {
      hasInitializedConditionsFromPolicy.current = true;

      setTriggerSubStep(
        policyData.triggerType === 'LABEL_MODIFICATION'
          ? 'labels_change'
          : 'agent_registered'
      );

      const conds = policyData.conditions;
      const rows: ConditionRow[] = (conds.conditions ?? [])
        .filter(
          (c): c is { field: string; operator: string; value: string } =>
            typeof c === 'object' && c !== null
        )
        .map((c, idx) => ({
          id: String(Date.now() + idx),
          attribute: c.field,
          operator: c.operator,
          value: c.value ?? '',
        }));
      setConditionData({
        conditions: rows.length > 0 ? rows : [createEmptyConditionRow()],
      });
    }

    if (policyId && !hasInitializedOwnersFromPolicy.current) {
      hasInitializedOwnersFromPolicy.current = true;
      const ownerAction = policyData.actions?.find(
        (a) => a.type === 'owner_assignment'
      );
      const initialPayload = ownerAction?.payload?.find(
        (p) => p.type === 'initial_assignment'
      );
      const value = initialPayload?.value ?? [];
      const ownerRows: OwnerRow[] = value.map(
        (
          item: {
            role?: string;
            rank?: number;
            assigned_to?: { user_id?: string };
          },
          idx: number
        ) => ({
          id: String(Date.now() + idx + 1000),
          role: (item.role ?? '').trim() || '',
          rank: String(item.rank ?? '').trim() || '',
          owner: (item.assigned_to?.user_id ?? '').trim() || '',
        })
      );
      const rows = ownerRows.length > 0 ? ownerRows : DEFAULT_OWNERS;
      setOwnerData(rows);

      // Resolve emails to display names
      const emails = rows.map((r) => r.owner).filter((id) => id.length > 0);
      if (emails.length > 0) {
        setIsResolvingOwners(true);
        identityApi
          .getIdentityList({
            pageSize: 100,
            filters: [{ field: 'email', operator: 'IN', values: emails }],
          })
          .then((opts) => {
            // Key by email since row.owner now stores email (from saved policy user_id)
            const byEmail = new Map(
              opts
                .filter((o) => o.secondaryLabel)
                .map((o) => [
                  o.secondaryLabel!,
                  { label: o.label, value: o.value },
                ])
            );
            if (byEmail.size > 0) {
              setOwnerData((prev) =>
                prev.map((row) =>
                  byEmail.has(row.owner)
                    ? {
                        ...row,
                        ownerLabel: byEmail.get(row.owner)!.label,
                        ownerEmail: row.owner,
                      }
                    : row
                )
              );
            }
          })
          .catch(() => {
            // Silently fail — owner emails will show as fallback
          })
          .finally(() => {
            setIsResolvingOwners(false);
          });
      }
    }
  }, [policyData, policyId]);

  const handleSavePolicy = () => {
    if (!conditionData) {
      return;
    }

    setSaveAttempted(false);

    const formattedConditions = {
      operator: 'AND',
      conditions: conditionData.conditions
        .filter((c) => !isRowIncomplete(c))
        .map((c) => ({
          field: c.attribute!.trim(),
          operator: c.operator as Operator,
          value: c.value ?? '',
        })),
    };

    const ownerAssignments = ownerData.filter(isOwnerRowValid).map((row) => ({
      role: (row.role ?? '').trim(),
      rank: Number((row.rank ?? '').trim()) || 1,
      assigned_to: {
        type: 'user',
        user_id: (row.ownerEmail ?? '').trim(),
      },
    }));

    const payload = {
      name: policyTitle,
      description: policyDescription,
      conditions: formattedConditions,
      actions: [
        {
          type: 'owner_assignment',
          payload: [
            {
              type: 'initial_assignment',
              value: ownerAssignments,
              payload: [],
            },
          ],
        },
      ],
      domain: 'AGENT_SECURITY',
      triggerType:
        triggerSubStep === 'labels_change'
          ? 'LABEL_MODIFICATION'
          : 'AGENT_REGISTERED',
      isActive: true,
      isCustom: true,
      batchModeEnabled: applyToExisting,
    };

    if (policyId) {
      updatePolicyMutate.mutate({ payload, policyId });
    } else {
      createPolicyMutate.mutate({ payload });
    }
  };

  // In edit mode, show loader until policy data and owner names are fully resolved
  const isEditLoading = !!policyId && (isPolicyLoading || isResolvingOwners);

  if (isEditLoading) {
    return (
      <Box className='PolicyForm--isLoading'>
        <Loader kind='loop' format='main' size='medium' color='brand' />
      </Box>
    );
  }

  return (
    <Box className='PolicyForm'>
      {/* Policy detail header */}
      <Box tag='header' className='PolicyForm-header'>
        <Box className='PolicyForm-headerLeft'>
          <ButtonIcon
            size='large'
            kind='ghost'
            icon={<Icon kind='ChevronLeft' size='medium' />}
            ariaLabel={intl.formatMessage({
              id: 'XsANoV',
              defaultMessage: 'Go back to previous page',
              description: 'Aria label for back button',
            })}
            onClick={() => navigate({ to: '/policies' })}
          />
          <Box className='PolicyForm-headerTitleWrap'>
            <Box className='PolicyForm-headerTitleRow'>
              <Typography
                kind='h2'
                color={TOKENS.CF_NEU_STL}
                className='PolicyForm-headerTitle'>
                {policyTitle}
              </Typography>
              <Box className='PolicyForm-headerEditBtn'>
                <ButtonIcon
                  size='medium'
                  kind='ghost'
                  icon={<Icon kind='Edit' size='small' />}
                  ariaLabel={intl.formatMessage({
                    id: 'DzvHlz',
                    defaultMessage: 'Edit rule title',
                    description: 'Edit policy title',
                  })}
                  onClick={() => setIsEditPolicyModalOpen(true)}
                />
              </Box>
            </Box>
            {policyDescription ? (
              <Typography
                kind='body2'
                color={TOKENS.CF_NEU_STL}
                className='PolicyForm-headerSubtitle'>
                {policyDescription}
              </Typography>
            ) : !policyId ? (
              <Typography
                kind='body2'
                color={TOKENS.CF_NEU_STL}
                className='PolicyForm-headerSubtitle'>
                <FormattedMessage
                  id='DYird9'
                  defaultMessage='Add a description'
                  description='Placeholder subtitle shown in create mode when no description is set'
                />
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box className='PolicyForm-headerActions'>
          <Button
            kind='filled'
            size='medium'
            leftIcon={<Icon kind='Activate' />}
            isDisabled={isSaveDisabled}
            onClick={handleSavePolicy}>
            <FormattedMessage
              id='Rf23tg'
              defaultMessage='Save and Activate'
              description='Save and activate policy button'
            />
          </Button>
          {isSaveDisabled && (
            <Tooltip
              text={saveTooltipText}
              className='PolicyForm-saveTooltip'
              classNames={{ containerClassName: 'PolicyForm-headerTooltip' }}
              maxWidth='20rem'
              isPortalEnabled>
              <span className='PolicyForm-saveTooltipTrigger'>
                <Icon
                  kind='AlertInfoFilled'
                  size='medium'
                  color={TOKENS.CF_INF_MED}
                />
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {banner && banner.colorTheme === 'Critical' && (
        <AlertBanner
          colorTheme='Critical'
          title={banner.title}
          isFloating={false}
          onCancel={() => setBanner(null)}
          isColonVisible={false}
        />
      )}

      <PolicyNameUpdateModal
        isOpen={isEditPolicyModalOpen}
        onClose={() => setIsEditPolicyModalOpen(false)}
        initialTitle={policyTitle}
        initialDescription={policyDescription}
        untitledLabel={untitledPolicyLabel}
        onSave={(title, description) => {
          setPolicyTitle(title);
          setPolicyDescription(description);
        }}
      />

      {/* Main content: steps + detail panel */}
      <Box className='PolicyForm-main'>
        <PolicyFormSteps
          selectedStep={selectedStep}
          onStepClick={setSelectedStep}
          conditionCount={conditionCount}
          businessOwnerCount={businessOwnerCount}
          technicalOwnerCount={technicalOwnerCount}
          selectedTrigger={triggerSubStep}
        />
        {selectedStep === 1 ? (
          triggerSubStep === null ? (
            <TriggerSelectContent onSelect={setTriggerSubStep} />
          ) : (
            <ConditionContent
              key={`${policyData?.id ?? 'create'}-${triggerSubStep}`}
              onConditionChange={setConditionData}
              previousConditions={policyData?.conditions}
              saveAttempted={saveAttempted}
              initialConditions={conditionData?.conditions}
              onBack={() => {
                setTriggerSubStep(null);
                setConditionData(null);
              }}
              triggerType={triggerSubStep}
            />
          )
        ) : (
          <ActionContent
            owners={ownerData}
            onOwnerChange={setOwnerData}
            saveAttempted={saveAttempted}
            applyToExisting={applyToExisting}
            onApplyToExistingChange={setApplyToExisting}
            matchingAgentCount={matchingAgentCount}
          />
        )}
      </Box>
    </Box>
  );
}
